import { useEffect, useMemo, useState } from 'react';
import {
    Button, Col, Form, Input, notification, PageHeader, Radio, Row,
    Space,
} from 'antd';
import {
    DrawingManager, GoogleMap, Polygon, InfoWindow, useJsApiLoader,
    Marker,
} from '@react-google-maps/api';

import { useDispatch } from 'react-redux';
import {
    addRegion,
    getRegionsByViewport,
    GetRegionsByViewportParams,
} from '../api/regions';
import useDebounce from '../hooks/useDebounce';
import {
    Region, CarType, PaymentType, RideType,
} from '../const/region';
import { GOOGLE_MAPS_KEY } from '../const/envs';
import RegionActionModal from '../components/Modals/RegionActionsModal';
import { openModal } from '../actions/modals';
import { ModalTypes } from '../types/modals';
import { checkRegionStatus } from './CommandCenter/utils/region';
import { googleMapsStyles } from '../components/Map/googleMapStyles';
import { getPathsFromPolygon } from '../utils/polygons';
import SearchLocation from '../components/DeliveryProviders/SearchLocation';
import { centerByCountry } from '../utils/countries';

const mapOptions = {
    styles: googleMapsStyles,
};

export const AddRegion = () => {
    const dispatch = useDispatch();

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_KEY,
        libraries: ['drawing', 'places'],
    });

    const [regions, setRegions] = useState<Region[]>([]);
    const [newPolygon, setNewPolygon] = useState<google.maps.Polygon>();
    const [zoom, setZoom] = useState(12);
    const [center, setCenter] = useState<any>(centerByCountry('EC' as string));
    const [mapInstance, setMapInstance] = useState<google.maps.Map>();
    const [pendingViewportParams, setPendingViewportParams] = useState<GetRegionsByViewportParams | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const debouncedViewportParams = useDebounce<GetRegionsByViewportParams | null>(pendingViewportParams, 800);
    const [showInfoWindow, setShowInfoWindow] = useState({
        show: false,
        lat: 0,
        lng: 0,
        regionName: '',
    });

    const fetchDeleteRegion = (id: string) => {
        setRegions(regions.filter((region) => region._id !== id));
    };

    const fetchGetRegionsByViewport = (params: GetRegionsByViewportParams) => {
        setIsLoading(true);
        getRegionsByViewport(params)
            .then((response) => {
                const payload: any = response.data;
                const fetched: Region[] = (payload?.regions as Region[]) || (payload?.data as Region[]) || [];
                setRegions((prev) => {
                    const existingIds = new Set(prev.map((r) => r._id));
                    const uniqueNew = fetched.filter((r) => !existingIds.has(r._id));
                    if (uniqueNew.length === 0) {
                        return prev;
                    }
                    return [...prev, ...uniqueNew];
                });
            })
            .catch(() => {})
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (!debouncedViewportParams) {
            return;
        }
        fetchGetRegionsByViewport(debouncedViewportParams);
    }, [debouncedViewportParams]);

    const onPolygonComplete = (polygon: google.maps.Polygon) => {
        setNewPolygon(polygon);
    };

    const onSubmit = async (values: any) => {
        if (!newPolygon) {
            notification.error({
                message: 'Error',
                description: 'No polygon selected',
            });
            return null;
        }
        try {
            const coordinates = getPathsFromPolygon(newPolygon);
            const payload = { ...values, role: 'admin', coordinates };
            const { data } = await addRegion(payload);
            setRegions([...regions, data.data]);
            newPolygon.setMap(null);
            notification.success({
                message: 'Region added',
                description: 'Region added successfully',
            });
        } catch (error: any) {
            const { message } = error.response.data;
            notification.error({
                message: 'Error',
                description: message,
            });
        } finally {
            newPolygon.setMap(null);
            setNewPolygon(undefined);
        }
    };

    const handleReset = () => {
        newPolygon?.setMap(null);
        setNewPolygon(undefined);
    };

    const handleUpdateLocation = ({ center, zoom }: { center: { lat: number; lng: number }; zoom: number }) => {
        setCenter(center);
        setZoom(zoom);
    };
    const handleMapLoad = (map: google.maps.Map) => {
        setMapInstance(map);
        const bounds = map.getBounds();
        if (!bounds) {
            return;
        }
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        setPendingViewportParams({
            northEastLat: ne.lat(),
            northEastLng: ne.lng(),
            southWestLat: sw.lat(),
            southWestLng: sw.lng(),
            limit: 100,
        });
    };

    const handleMapIdle = () => {
        if (!mapInstance) {
            return;
        }
        const bounds = mapInstance.getBounds();
        if (!bounds) {
            return;
        }
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        setPendingViewportParams({
            northEastLat: ne.lat(),
            northEastLng: ne.lng(),
            southWestLat: sw.lat(),
            southWestLng: sw.lng(),
            limit: 100,
        });
    };

    const setDefaultLocation = () => {
        setCenter(centerByCountry('EC' as string));
        setZoom(12);
    };

    const regionToMap = useMemo(() => regions.map((region) => ({
        ...region,
        paths: region.geoLongLat.coordinates[0].map((coordinate: number[]) => ({ lat: coordinate[1], lng: coordinate[0] })),
    })), [regions]);

    return (
        <div>
            <PageHeader
                className="site-page-header"
                title="Add region"
            />
            <RegionActionModal
                onSuccess={
                    (region: Region) => {
                    // update region in regions
                        setRegions(regions.map((r) => (r._id === region._id ? region : r)));
                    }
                }
                onSuccessDelete={fetchDeleteRegion}
            />
            <Form onFinish={onSubmit} layout="vertical" requiredMark={false}>
                <Row gutter={20}>
                    <Col xs={24} lg={8}>
                        <Form.Item name="regionName" label="Area name" rules={[{ required: true }]}>
                            <Input placeholder="Region 1" />
                        </Form.Item>
                        <Form.Item label="Car type" name="carType" initialValue={CarType.BIKE}>
                            <Radio.Group>
                                <Radio value={CarType.BIKE}>Bike</Radio>
                                <Radio value={CarType.LITE}>Lite</Radio>
                                <Radio value={CarType.BOTH}>Both</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item label="Ride type" name="rideType" initialValue={1}>
                            <Radio.Group>
                                <Radio value={RideType.RIDE_NOW}>Ride Now</Radio>
                                <Radio value={RideType.RIDE_LATER}>Ride Later</Radio>
                                <Radio value={RideType.BOTH}>Both</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item label="Payment type" name="paymentType" initialValue={1}>
                            <Radio.Group>
                                <Radio value={PaymentType.CARD}>Card</Radio>
                                <Radio value={PaymentType.CASH}>Cash</Radio>
                                <Radio value={PaymentType.BOTH}>Both</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Row gutter={10}>
                            <Col>
                                <Button onClick={handleReset}>
                                    Reset
                                </Button>
                            </Col>
                            <Col>
                                <Form.Item>
                                    <Button type="primary" htmlType="submit">
                                        Submit
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={24} lg={16}>
                        <Space direction="vertical" style={{ width: '100%' }} size={16}>
                            <Form.Item label="Search location" name="location">
                                <SearchLocation
                                    updateLocation={handleUpdateLocation}
                                    setDefaultLocation={setDefaultLocation}
                                    isLoading={isLoading}
                                />
                            </Form.Item>
                            {isLoaded && (
                                <GoogleMap
                                    mapContainerStyle={{ height: '68vh', width: '100%' }}
                                    options={mapOptions}
                                    zoom={zoom}
                                    center={center}
                                    onLoad={handleMapLoad}
                                    onIdle={handleMapIdle}
                                >

                                    <Marker
                                        position={center}
                                    />

                                    <DrawingManager
                                        onPolygonComplete={onPolygonComplete}
                                        options={{
                                            drawingControl: true,
                                            drawingControlOptions: {
                                                drawingModes: ['polygon' as any],
                                            },
                                        }}
                                    />

                                    {
                                        showInfoWindow.show && (
                                            <InfoWindow
                                                position={{
                                                    lat: showInfoWindow.lat,
                                                    lng: showInfoWindow.lng,
                                                }}
                                                onCloseClick={() => setShowInfoWindow({ ...showInfoWindow, show: false })}
                                            >
                                                <div style={{ color: '#1A1F36' }}>
                                                    {showInfoWindow.regionName}
                                                </div>
                                            </InfoWindow>
                                        )
                                    }

                                    {
                                        regionToMap.map((region: any) => {
                                            const { fill: fillColor, stroke: strokeColor } = checkRegionStatus(region.regionStatus);
                                            return (
                                                <Polygon
                                                    onClick={() => dispatch(openModal(ModalTypes.RegionActions, { region, allRegions: regions }))}
                                                    key={region._id}
                                                    paths={region.paths}
                                                    options={{
                                                        fillColor,
                                                        strokeColor,
                                                    }}
                                                    onMouseOver={(data: any) => {
                                                        setShowInfoWindow({
                                                            lat: data.latLng.lat(),
                                                            lng: data.latLng.lng(),
                                                            show: true,
                                                            regionName: region.regionName,
                                                        });
                                                    }}
                                                    onMouseOut={() => {
                                                        setShowInfoWindow({
                                                            ...showInfoWindow,
                                                            show: false,
                                                        });
                                                    }}
                                                />
                                            );
                                        })
                                    }
                                </GoogleMap>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};
